import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { getModuleDisplayName } from '@/lib/modules';
import { companyRoleToModuleRole } from '@/lib/module-roles';
import type { ModuleRoleLevel, UserRole } from '@/lib/types';

function moduleRoleLabel(role: ModuleRoleLevel): string {
  if (role === 'no_access') return 'No Access';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

interface ModuleRoleSyncDialogProps {
  open: boolean;
  userName: string;
  newRole: UserRole;
  modules: string[];
  currentRoles: Record<string, ModuleRoleLevel>;
  isUpdating: boolean;
  onUpdate: () => void;
  onKeep: () => void;
}

export function ModuleRoleSyncDialog({
  open,
  userName,
  newRole,
  modules,
  currentRoles,
  isUpdating,
  onUpdate,
  onKeep,
}: ModuleRoleSyncDialogProps) {
  const newModuleRole = companyRoleToModuleRole(newRole);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Also update {userName}&apos;s module access to match {newRole}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Review the enabled modules before replacing the current access.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="divide-y rounded-md border">
          {modules.map((moduleKey) => {
            const currentRole = currentRoles[moduleKey] ?? 'no_access';
            return (
              <div
                key={moduleKey}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
              >
                <span className="font-medium">{getModuleDisplayName(moduleKey)}</span>
                <span className="text-muted-foreground">
                  {moduleRoleLabel(currentRole)} → {moduleRoleLabel(newModuleRole)}
                </span>
              </div>
            );
          })}
          {modules.length === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              No modules are enabled for this organization.
            </p>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onKeep} disabled={isUpdating}>
            Keep current module access
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              onUpdate();
            }}
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Update modules'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
